"use client"

import { useMemo, useState } from "react"
import Link from "next/link"
import {
  ArrowRight,
  BookOpen,
  Building2,
  CalendarDays,
  Car,
  ChevronDown,
  CircleUserRound,
  Cpu,
  Film,
  Gamepad2,
  Globe2,
  Home,
  LayoutGrid,
  Music2,
  PawPrint,
  Search,
  SlidersHorizontal,
  Sparkles,
  Star,
  Swords,
  Trophy,
  UsersRound,
  Youtube,
} from "lucide-react"
import { ResilientFillImage } from "@/components/media/resilient-fill-image"
import styles from "@/app/play/play-page.module.css"

export interface PlayCategory {
  id: string
  name: string
  slug: string
  description: string | null
  image: string | null
  icon: string | null
  gameCount: number
  itemCount: number
  roomCount: number
}

const iconMap: Record<string, React.ElementType> = {
  home: Home,
  users: UsersRound,
  globe: Globe2,
  "trending-up": Cpu,
  "dollar-sign": SlidersHorizontal,
  music: Music2,
  film: Film,
  gamepad: Gamepad2,
  utensils: Sparkles,
  car: Car,
  plane: Globe2,
  star: Star,
  youtube: Youtube,
  trophy: Trophy,
  building: Building2,
  "book-open": BookOpen,
  "paw-print": PawPrint,
  sparkles: Sparkles,
}

const modes = [
  {
    name: "Solo",
    description: "Build your personal best streak.",
    href: "#categories",
    icon: CircleUserRound,
    active: true,
  },
  {
    name: "Daily",
    description: "One shared challenge every day.",
    href: "/daily",
    icon: CalendarDays,
    active: false,
  },
  {
    name: "Versus",
    description: "Open a live room for your group.",
    href: "/versus",
    icon: Swords,
    active: false,
  },
  {
    name: "Custom",
    description: "Build and share your own game.",
    href: "/custom/create",
    icon: SlidersHorizontal,
    active: false,
  },
] as const

type SortMode = "popular" | "alphabetical" | "games" | "items"

const DEFAULT_CATEGORY_COUNT = 11

export function PlayDiscovery({
  categories,
}: {
  categories: PlayCategory[]
}) {
  const [query, setQuery] = useState("")
  const [sort, setSort] = useState<SortMode>("popular")
  const [showAll, setShowAll] = useState(false)

  const filteredCategories = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    const matches = categories.filter((category) => {
      if (!normalizedQuery) return true
      return `${category.name} ${category.description ?? ""}`
        .toLowerCase()
        .includes(normalizedQuery)
    })

    return matches.sort((a, b) => {
      if (sort === "alphabetical") return a.name.localeCompare(b.name)
      if (sort === "games") {
        return b.gameCount - a.gameCount || a.name.localeCompare(b.name)
      }
      if (sort === "items") {
        return b.itemCount - a.itemCount || a.name.localeCompare(b.name)
      }
      return b.roomCount - a.roomCount || a.name.localeCompare(b.name)
    })
  }, [categories, query, sort])

  const visibleCategories =
    showAll || query
      ? filteredCategories
      : filteredCategories.slice(0, DEFAULT_CATEGORY_COUNT)
  const hiddenCount = Math.max(
    filteredCategories.length - DEFAULT_CATEGORY_COUNT,
    0,
  )
  const popularCategories = [...categories]
    .sort((a, b) => b.roomCount - a.roomCount)
    .slice(0, 5)

  return (
    <>
      <section
        className={styles.modeSection}
        id="play-modes"
        aria-labelledby="mode-title"
      >
        <h2 id="mode-title">How do you want to play?</h2>
        <div className={styles.modeGrid}>
          {modes.map((mode) => {
            const Icon = mode.icon
            return (
              <Link
                href={mode.href}
                key={mode.name}
                className={styles.modeCard}
                data-active={mode.active ? "true" : undefined}
                aria-label={`${mode.name}. ${mode.description}`}
              >
                <Icon aria-hidden="true" />
                <strong>{mode.name}</strong>
                <span>{mode.description}</span>
                <span className={styles.modeArrow} aria-hidden="true">
                  <ArrowRight />
                </span>
              </Link>
            )
          })}
        </div>
      </section>

      <section
        className={styles.categorySection}
        id="categories"
        aria-labelledby="category-title"
      >
        <div className={styles.categoryHeader}>
          <h2 id="category-title">Browse all categories</h2>
          <div className={styles.categoryControls}>
            <label className={styles.searchField}>
              <Search aria-hidden="true" />
              <span className="sr-only">Search categories</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search categories"
              />
            </label>
            <label className={styles.sortField}>
              <span className="sr-only">Sort categories</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as SortMode)}
              >
                <option value="popular">Most played</option>
                <option value="alphabetical">A to Z</option>
                <option value="games">Most games</option>
                <option value="items">Most items</option>
              </select>
              <ChevronDown aria-hidden="true" />
            </label>
          </div>
        </div>

        {visibleCategories.length ? (
          <div className={styles.categoryGrid} aria-live="polite">
            {visibleCategories.map((category, index) => {
              const Icon =
                iconMap[category.icon?.toLowerCase() || "globe"] || Globe2
              return (
                <Link
                  href={`/play/${category.slug}`}
                  key={category.id}
                  className={styles.categoryCard}
                  style={{ "--card-index": index } as React.CSSProperties}
                >
                  <ResilientFillImage
                    src={category.image}
                    alt=""
                    fallbackText={category.name.slice(0, 2).toUpperCase()}
                    fallbackClassName={styles.categoryFallback}
                    imageClassName={styles.categoryImage}
                    sizes="(max-width: 620px) 100vw, (max-width: 940px) 50vw, (max-width: 1280px) 33vw, 25vw"
                    optimizedWidth={640}
                  />
                  <span className={styles.categoryShade} aria-hidden="true" />
                  <span className={styles.categoryIcon} aria-hidden="true">
                    <Icon />
                  </span>
                  <span className={styles.categoryCopy}>
                    <strong>{category.name}</strong>
                    <small>
                      {category.gameCount}{" "}
                      {category.gameCount === 1 ? "game" : "games"},{" "}
                      {category.itemCount.toLocaleString()} items
                    </small>
                  </span>
                </Link>
              )
            })}

            {!query && hiddenCount > 0 && !showAll ? (
              <button
                type="button"
                className={styles.moreCard}
                onClick={() => setShowAll(true)}
              >
                <LayoutGrid aria-hidden="true" />
                <strong>More</strong>
                <span>Show {hiddenCount} more categories</span>
                <ArrowRight aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : (
          <div className={styles.emptyState} role="status">
            <Search aria-hidden="true" />
            <strong>No categories found</strong>
            <span>Try a shorter or more general search.</span>
            <button type="button" onClick={() => setQuery("")}>
              Clear search
            </button>
          </div>
        )}

        {!query && showAll && hiddenCount > 0 ? (
          <button
            type="button"
            className={styles.showLess}
            onClick={() => setShowAll(false)}
          >
            Show fewer categories
          </button>
        ) : null}

        <div className={styles.popular}>
          <div className={styles.popularHeader}>
            <div>
              <h3>Popular right now</h3>
              <p>Ranked by multiplayer rooms opened.</p>
            </div>
            <Link href="/leaderboard">
              View leaderboard
              <Trophy aria-hidden="true" />
            </Link>
          </div>
          <ol className={styles.popularList}>
            {popularCategories.map((category, index) => (
              <li key={category.id}>
                <Link href={`/play/${category.slug}`}>
                  <span className={styles.popularImage}>
                    <ResilientFillImage
                      src={category.image}
                      alt=""
                      fallbackText={category.name.slice(0, 2).toUpperCase()}
                      fallbackClassName={styles.categoryFallback}
                      imageClassName={styles.categoryImage}
                      sizes="120px"
                      optimizedWidth={256}
                    />
                    <b>{index + 1}</b>
                  </span>
                  <span>
                    <strong>{category.name}</strong>
                    <small>
                      {category.roomCount.toLocaleString()} rooms played
                    </small>
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </>
  )
}
